/**
 * Seating Map - Philippe Chatrier
 * Interactive SVG seating chart for French Open tickets
 *
 * Categories match actual product variants:
 * - BOX/LOGE: VIP courtside boxes with hospitality
 * - CAT Gold: Premium lower level (rows 1-9)
 * - CAT 1: Lower level (rows 10-21)
 * - CAT 2: Upper level (rows 1-13)
 * - CAT 3: Upper level (remaining rows)
 */

(function() {
  'use strict';

  // Category configuration matching actual product variants
  // Colors match the actual SVG map fill colors
  const CATEGORIES = {
    'box-loge': {
      name: 'Box/Loge',
      description: 'VIP courtside box seats with hospitality. First 8 rows closest to the court with premium amenities.',
      color: '#4a9f4a',  // Green - matches map SVG
      // Matches: "BOX with Hospitality", "BOX/LOGE"
      searchTerms: ['box', 'loge', 'hospitality']
    },
    'cat-gold': {
      name: 'Category Gold',
      description: 'Premium lower level seating, rows 1-9. Excellent views just behind the box seats.',
      color: '#d67c38',  // Orange - matches map SVG
      // Matches: "CAT Gold row 9", "Category Gold"
      searchTerms: ['cat gold', 'gold row', 'category gold']
    },
    'cat-1': {
      name: 'Category 1',
      description: 'Lower level seating, rows 10-21. Great views with close proximity to the court.',
      color: '#e8c858',  // Yellow/gold - matches map SVG
      // Matches: "CAT 1 row 21", "Category 1"
      searchTerms: ['cat 1 ', 'cat 1\n', 'category 1']
    },
    'cat-2': {
      name: 'Category 2',
      description: 'Upper level seating, rows 1-13. Excellent elevated views of the entire court.',
      color: '#FBCFD1',  // Pink - matches map SVG
      // Matches: "CAT 2 row 13", "Category 2"
      searchTerms: ['cat 2', 'category 2']
    },
    'cat-3': {
      name: 'Category 3',
      description: 'Upper level seating, remaining rows. Panoramic views with great value pricing.',
      color: '#52a5b8',  // Blue/teal - matches map SVG
      // Matches: "CAT 3 row 18", "Category 3"
      searchTerms: ['cat 3', 'category 3']
    }
  };

  // Tribune names for tooltip
  const TRIBUNE_NAMES = {
    'borotra': 'Tribune J. Borotra',
    'brugnon': 'Tribune J. Brugnon',
    'cochet': 'Tribune H. Cochet',
    'lacoste': 'Tribune R. Lacoste',
    'loge': 'Loge Box'
  };

  class SeatingMap {
    constructor() {
      this.map = document.getElementById('chatrier-map');
      this.tooltip = document.getElementById('map-tooltip');
      this.ticketsPanel = document.getElementById('seating-map-tickets');
      this.sections = null;
      this.legendItems = null;
      this.activeCategory = null;
      this.activeBlock = null;
      this.zoomLevel = 1;
      this.maxZoom = 2;
      this.minZoom = 0.8;

      if (!this.map) return;

      this.init();
    }

    init() {
      this.sections = this.map.querySelectorAll('.seat-section');
      this.legendItems = document.querySelectorAll('.legend-item');
      this.priceRanges = {}; // Store price ranges per category

      this.bindEvents();
      this.initZoomControls();
      this.calculatePriceRanges(); // Calculate price ranges for tooltips
      this.markSoldOutSections(); // Grey out map sections with no availability
      this.renderPriceLabels(); // Render "From $X" labels on map
      this.hideSoldOutCategories(); // Hide legend items for categories with no tickets
      this.showAllTickets(); // Show all tickets initially
      this.setupSortControls(); // Sort dropdown for sidebar
      this.hideDuplicateTicketSection(); // Hide the duplicate ticket section below the map
    }

    // Calculate price ranges for each category from ticket cards
    calculatePriceRanges() {
      const ticketCards = this.getOriginalTicketCards();

      Object.keys(CATEGORIES).forEach(category => {
        this.priceRanges[category] = { min: Infinity, max: -Infinity, count: 0 };
      });

      ticketCards.forEach(card => {
        // Prefer data-category attribute, fall back to text detection
        const dataCat = (card.getAttribute('data-category') || '').toLowerCase();
        const category = (dataCat && CATEGORIES[dataCat]) ? dataCat : this.detectCardCategory(card.textContent);
        if (!category) return;

        // Use data-price attribute (cents) for accuracy, fall back to text parsing
        const priceCents = parseFloat(card.getAttribute('data-price'));
        if (!isNaN(priceCents) && priceCents > 0) {
          const price = priceCents / 100;
          this.priceRanges[category].min = Math.min(this.priceRanges[category].min, price);
          this.priceRanges[category].max = Math.max(this.priceRanges[category].max, price);
          this.priceRanges[category].count++;
        } else {
          const priceEl = card.querySelector('.price-value');
          if (priceEl) {
            const priceText = priceEl.textContent.replace(/[^0-9.,]/g, '').replace(',', '');
            const price = parseFloat(priceText);
            if (!isNaN(price)) {
              this.priceRanges[category].min = Math.min(this.priceRanges[category].min, price);
              this.priceRanges[category].max = Math.max(this.priceRanges[category].max, price);
              this.priceRanges[category].count++;
            }
          }
        }
      });
    }

    // Grey out map sections whose category has zero available listings
    // (includes non-purchasable areas like Presidential)
    markSoldOutSections() {
      if (!this.sections) return;
      this.sections.forEach(section => {
        const cat = section.dataset.category;
        const pr = this.priceRanges[cat];
        const soldOut = !pr || pr.count === 0;
        section.classList.toggle('smux-soldout', soldOut);
      });
    }

    // Hide legend items for categories with no tickets and add price ranges
    hideSoldOutCategories() {
      const ticketCards = this.getOriginalTicketCards();
      const categoriesWithTickets = new Set();

      ticketCards.forEach(card => {
        // Prefer data-category attribute (set by Liquid), fall back to text detection
        const dataCat = (card.getAttribute('data-category') || '').toLowerCase();
        if (dataCat && CATEGORIES[dataCat]) {
          categoriesWithTickets.add(dataCat);
        } else {
          const category = this.detectCardCategory(card.textContent);
          if (category) {
            categoriesWithTickets.add(category);
          }
        }
      });

      this.legendItems.forEach(item => {
        const category = item.dataset.category;
        if (!categoriesWithTickets.has(category)) {
          item.style.display = 'none';
        } else {
          // Add price range to legend item
          this.addPriceToLegendItem(item, category);
        }
      });
    }

    // Add price range display to legend item
    addPriceToLegendItem(item, category) {
      const priceRange = this.priceRanges[category];
      if (!priceRange || priceRange.count === 0) return;

      // Check if price already added
      if (item.querySelector('.legend-price')) return;

      const priceEl = document.createElement('span');
      priceEl.className = 'legend-price';

      if (priceRange.min === priceRange.max) {
        priceEl.textContent = `$${Math.round(priceRange.min)}`;
      } else {
        priceEl.textContent = `$${Math.round(priceRange.min)}+`;
      }

      item.appendChild(priceEl);
    }

    // Hide the duplicate ticket selection section since tickets are shown in the map sidebar
    hideDuplicateTicketSection() {
      const duplicateSection = document.querySelector('.ticket-selection-wrapper');
      if (duplicateSection) {
        duplicateSection.style.display = 'none';
      }
      // Also hide the marketplace section if present
      const marketplaceSection = document.querySelector('.ticket-variants-marketplace-section');
      if (marketplaceSection) {
        marketplaceSection.style.display = 'none';
      }
    }

    bindEvents() {
      // Section hover and click events
      this.sections.forEach(section => {
        section.addEventListener('mouseenter', (e) => this.handleSectionHover(e, section));
        section.addEventListener('mouseleave', () => this.hideTooltip());
        section.addEventListener('click', () => this.handleSectionClick(section));
      });

      // Legend click events
      this.legendItems.forEach(item => {
        item.addEventListener('click', () => {
          const category = item.dataset.category;
          this.selectCategory(category);
        });
      });

      // v2026-04-23: exclude stand/date pills so their highlight isn't cleared
      // v2026-08: exclude the whole listings sidebar + sticky bar — clicking a
      // card, accordion header, qty chip or sort select must NOT reset the filter
      document.addEventListener('click', (e) => {
        const t = e.target;
        if (t.closest('.seating-map-wrapper') ||
            t.closest('.seating-map-right') ||
            t.closest('.smux-sticky') ||
            t.closest('.legend-item') ||
            t.closest('.category-info-panel') ||
            t.closest('.stand-btn') ||
            t.closest('.stand-selector') ||
            t.closest('.date-btn') ||
            t.closest('.date-filter')) {
          return;
        }
        this.clearSelection();
      });
    }

    initZoomControls() {
      const zoomIn = document.getElementById('map-zoom-in');
      const zoomOut = document.getElementById('map-zoom-out');
      const reset = document.getElementById('map-reset');

      if (zoomIn) {
        zoomIn.addEventListener('click', () => this.zoom(0.2));
      }
      if (zoomOut) {
        zoomOut.addEventListener('click', () => this.zoom(-0.2));
      }
      if (reset) {
        reset.addEventListener('click', () => this.resetZoom());
      }
    }

    zoom(delta) {
      this.zoomLevel = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoomLevel + delta));
      this.map.style.transform = `scale(${this.zoomLevel})`;
    }

    resetZoom() {
      this.zoomLevel = 1;
      this.map.style.transform = 'scale(1)';
    }

    handleSectionHover(e, section) {
      const category = section.dataset.category;
      const blockId = section.dataset.block;

      // Build tooltip text: "Cat 2 block 40" format
      let categoryName = CATEGORIES[category]?.name || category;

      // Format: "Cat 2 block 40" or "Box Lacoste" for named boxes
      let tooltipText;
      if (blockId) {
        // Check if it's a named box (Box Borotra, Box Lacoste, etc.) or Presidential
        if (blockId.startsWith('Box ') || blockId === 'Presidential') {
          tooltipText = blockId;
        } else {
          // Regular numbered block: "Cat 2 block 40"
          tooltipText = `${categoryName} block ${blockId}`;
        }
      } else {
        tooltipText = categoryName;
      }

      // Consistent tooltip: section · from $X · N listings (or Sold out)
      const priceRange = this.priceRanges[category];
      if (priceRange && priceRange.count > 0) {
        const fromPrice = this.formatMapPrice(Math.round(priceRange.min * 100));
        const listingWord = priceRange.count === 1 ? 'listing' : 'listings';
        tooltipText += ` • from ${fromPrice} • ${priceRange.count} ${listingWord}`;
      } else {
        tooltipText += ' • Sold out';
      }

      this.showTooltip(e, tooltipText);
    }

    showTooltip(e, text) {
      if (!this.tooltip) return;

      this.tooltip.textContent = text;
      this.tooltip.classList.add('visible');

      // Position tooltip
      const rect = this.map.parentElement.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top - 40;

      this.tooltip.style.left = `${x}px`;
      this.tooltip.style.top = `${y}px`;
      this.tooltip.style.transform = 'translateX(-50%)';
    }

    hideTooltip() {
      if (this.tooltip) {
        this.tooltip.classList.remove('visible');
      }
    }

    handleSectionClick(section) {
      const category = section.dataset.category;
      const block = section.dataset.block;

      // For Box/Loge, use specific box filtering if clicking a named box
      if (category === 'box-loge' && block && block.startsWith('Box ')) {
        this.selectBox(block);
      } else {
        this.selectCategory(category);
      }
    }

    selectCategory(category) {
      this.activeCategory = category;
      this.activeBlock = null;

      // Update sections
      this.sections.forEach(section => {
        const sectionCategory = section.dataset.category;
        section.classList.remove('active', 'dimmed');

        if (sectionCategory === category) {
          section.classList.add('active');
        } else {
          section.classList.add('dimmed');
        }
      });

      // Update legend
      this.legendItems.forEach(item => {
        item.classList.toggle('active', item.dataset.category === category);
      });

      // Show matching tickets in sidebar and highlight on page
      this.showCategoryTickets(category);
      this.highlightTickets(category);
    }

    // Select a specific box (for Box/Loge tickets)
    selectBox(boxName) {
      this.activeCategory = 'box-loge';
      this.activeBlock = boxName;

      // Highlight only the matching box on the map
      this.sections.forEach(section => {
        section.classList.remove('active', 'dimmed');
        if (section.dataset.block === boxName) {
          section.classList.add('active');
        } else {
          section.classList.add('dimmed');
        }
      });

      // Update legend to show box-loge as active
      this.legendItems.forEach(item => {
        item.classList.toggle('active', item.dataset.category === 'box-loge');
      });

      // Show only tickets for this box
      this.showBoxTickets(boxName);
    }

    clearSelection() {
      this.activeCategory = null;
      this.activeBlock = null;

      this.sections.forEach(section => {
        section.classList.remove('active', 'dimmed');
      });

      this.legendItems.forEach(item => {
        item.classList.remove('active');
      });

      // Clear ticket highlighting
      const ticketCards = document.querySelectorAll('.ticket-variant-card');
      ticketCards.forEach(card => {
        card.classList.remove('highlight', 'dimmed');
      });

      this.updatePriceLabelVisibility(null);
      this.showAllTickets();
    }

    highlightTickets(category) {
      const categoryData = CATEGORIES[category];
      if (!categoryData) return;

      const ticketCards = document.querySelectorAll('.ticket-variant-card');

      ticketCards.forEach(card => {
        const cardText = card.textContent.toLowerCase();

        // Same matching logic as getTicketStats
        let categoryMatch = false;

        if (category === 'cat-1') {
          categoryMatch = /cat\s*1(?:\s|$|row)/i.test(cardText) && !/cat\s*1\d/i.test(cardText);
        } else if (category === 'cat-gold') {
          categoryMatch = /cat\s*gold/i.test(cardText);
        } else if (category === 'box-loge') {
          categoryMatch = /\bbox\b/i.test(cardText) || /\bloge\b/i.test(cardText);
        } else {
          categoryMatch = categoryData.searchTerms.some(term => cardText.includes(term.toLowerCase()));
        }

        if (categoryMatch) {
          card.classList.add('highlight');
          card.classList.remove('dimmed');
        } else {
          card.classList.remove('highlight');
          card.classList.add('dimmed');
        }
      });
    }

    scrollToCategory(category) {
      const ticketSection = document.getElementById('ticket-selection');
      if (ticketSection) {
        ticketSection.scrollIntoView({ behavior: 'smooth', block: 'start' });

        // Give time for scroll, then highlight
        setTimeout(() => {
          this.highlightTickets(category);
        }, 500);
      }
    }

    // Check if a card matches a category
    matchCategory(cardText, category) {
      const categoryData = CATEGORIES[category];
      if (!categoryData) return false;

      if (category === 'cat-1') {
        return /cat\s*1(?:\s|$|row)/i.test(cardText) && !/cat\s*1\d/i.test(cardText);
      } else if (category === 'cat-gold') {
        return /cat\s*gold/i.test(cardText);
      } else if (category === 'box-loge') {
        return /\bbox\b/i.test(cardText) || /\bloge\b/i.test(cardText);
      } else {
        return categoryData.searchTerms.some(term => cardText.includes(term.toLowerCase()));
      }
    }

    // Get original ticket cards (not from sidebar)
    getOriginalTicketCards() {
      // Select cards that are NOT inside the seating-map-tickets panel
      const allCards = document.querySelectorAll('.ticket-variant-card');
      return Array.from(allCards).filter(card => !card.closest('#seating-map-tickets'));
    }

    // Detect category from card text
    detectCardCategory(cardText) {
      const text = cardText.toLowerCase();

      if (/\bbox\b/i.test(text) || /\bloge\b/i.test(text)) {
        return 'box-loge';
      } else if (/cat\s*gold/i.test(text)) {
        return 'cat-gold';
      } else if (/cat\s*1(?:\s|$|row)/i.test(text) && !/cat\s*1\d/i.test(text)) {
        return 'cat-1';
      } else if (/cat\s*2/i.test(text)) {
        return 'cat-2';
      } else if (/cat\s*3/i.test(text)) {
        return 'cat-3';
      }
      return null;
    }

    // Detect box name from card (for Box/Loge tickets only)
    // Variant titles may be "BOX Cochet", "BOX Lacoste", or just "Lacoste", "Cochet" (stand name)
    detectCardBox(card) {
      // Check data-block attribute first
      // Only return block for actual box/loge cards (not regular numbered blocks)
      const cardCat = (card.getAttribute('data-category') || '').toLowerCase();
      const blockAttr = card.dataset.block;
      if (cardCat === 'box-loge' && blockAttr && blockAttr !== '') {
        return blockAttr;
      }

      // Check data-stand attribute (set by box seats filter)
      const standAttr = card.dataset.stand;
      if (standAttr && standAttr !== '') {
        return 'Box ' + standAttr; // Convert "Lacoste" to "Box Lacoste"
      }

      // Fallback: parse from card text (case-insensitive)
      const cardText = card.textContent.toLowerCase();

      // First try with "box" prefix
      const boxMapping = {
        'box cochet': 'Box Cochet',
        'box lacoste': 'Box Lacoste',
        'box borotra': 'Box Borotra',
        'box brugnon': 'Box Brugnon'
      };

      for (const [keyword, boxName] of Object.entries(boxMapping)) {
        if (cardText.includes(keyword)) {
          return boxName;
        }
      }

      // Then try without "box" prefix (for box seats page with stand names only)
      // Check for stand names at word boundaries to avoid false matches
      if (/\blacoste\b/.test(cardText)) {
        return 'Box Lacoste';
      }
      if (/\bcochet\b/.test(cardText)) {
        return 'Box Cochet';
      }

      return null;
    }

    // Apply category color to card header
    applyCardCategoryColor(card) {
      const cardText = card.textContent;
      const category = this.detectCardCategory(cardText);

      if (category && CATEGORIES[category]) {
        const color = CATEGORIES[category].color;
        const header = card.querySelector('.category-header');
        if (header) {
          header.style.backgroundColor = color;
          header.style.color = this.getContrastColor(color);
        }
      }
    }

    // Get contrasting text color (white or dark)
    getContrastColor(hexColor) {
      const r = parseInt(hexColor.slice(1, 3), 16);
      const g = parseInt(hexColor.slice(3, 5), 16);
      const b = parseInt(hexColor.slice(5, 7), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5 ? '#1a1a1a' : '#ffffff';
    }

    // Show all tickets in the sidebar
    showAllTickets() {
      if (!this.ticketsPanel) return;

      const ticketCards = this.getOriginalTicketCards();
      if (ticketCards.length === 0) {
        this.ticketsPanel.innerHTML = `
          <div class="seating-map-tickets__empty">
            <div class="seating-map-tickets__empty-icon">🎟️</div>
            <div class="seating-map-tickets__empty-text">No tickets available</div>
          </div>
        `;
        return;
      }

      // Clone and display all tickets
      this.ticketsPanel.innerHTML = '';
      ticketCards.forEach(card => {
        const clone = card.cloneNode(true);
        clone.classList.remove('highlight', 'dimmed');
        this.applyCardCategoryColor(clone);
        // Default quantity to 2, but clamp to available stock (max attribute)
        const qtyInput = clone.querySelector('.qty-input');
        if (qtyInput) {
          const maxQty = parseInt(qtyInput.max, 10) || 2;
          qtyInput.value = Math.min(2, maxQty);
        }
        this.ticketsPanel.appendChild(clone);
      });

      this.bindTicketEvents();
      this.inlineTrustLine();
      this.rewriteBuyNow();
      this.applySidebarSort();
      this.updatePriceLabelVisibility(null);
    }

    // Show only tickets for a specific category
    showCategoryTickets(category) {
      if (!this.ticketsPanel) return;

      const ticketCards = this.getOriginalTicketCards();
      const matchingCards = [];

      ticketCards.forEach(card => {
        // Prefer data-category attribute for matching
        const dataCat = (card.getAttribute('data-category') || '').toLowerCase();
        if (dataCat === category) {
          matchingCards.push(card);
        } else if (this.matchCategory(card.textContent.toLowerCase(), category)) {
          matchingCards.push(card);
        }
      });

      if (matchingCards.length === 0) {
        const categoryData = CATEGORIES[category];
        this.ticketsPanel.innerHTML = `
          <div class="seating-map-tickets__empty">
            <div class="seating-map-tickets__empty-icon">🎟️</div>
            <div class="seating-map-tickets__empty-text">No ${categoryData?.name || category} tickets available</div>
          </div>
        `;
        return;
      }

      // Clone and display matching tickets
      this.ticketsPanel.innerHTML = '';
      matchingCards.forEach(card => {
        const clone = card.cloneNode(true);
        clone.classList.add('highlight');
        clone.classList.remove('dimmed');
        this.applyCardCategoryColor(clone);
        // Default quantity to 2, but clamp to available stock (max attribute)
        const qtyInput = clone.querySelector('.qty-input');
        if (qtyInput) {
          const maxQty = parseInt(qtyInput.max, 10) || 2;
          qtyInput.value = Math.min(2, maxQty);
        }
        this.ticketsPanel.appendChild(clone);
      });

      this.bindTicketEvents();
      this.inlineTrustLine();
      this.rewriteBuyNow();
      this.applySidebarSort();
      this.updatePriceLabelVisibility(category);
      this.scrollToFirstCard();
    }

    // Show only tickets for a specific box
    showBoxTickets(boxName) {
      if (!this.ticketsPanel) return;

      const ticketCards = this.getOriginalTicketCards();
      const matchingCards = ticketCards.filter(card => {
        return this.detectCardBox(card) === boxName;
      });

      if (matchingCards.length === 0) {
        // No tickets for this box, show all Box/Loge
        this.ticketsPanel.innerHTML = `
          <div class="seating-map-tickets__empty">
            <div class="seating-map-tickets__empty-icon">🎟️</div>
            <div class="seating-map-tickets__empty-text">No ${boxName} tickets available</div>
          </div>
        `;
        return;
      }

      // Clone and display matching tickets
      this.ticketsPanel.innerHTML = '';
      matchingCards.forEach(card => {
        const clone = card.cloneNode(true);
        clone.classList.add('highlight');
        clone.classList.remove('dimmed');
        this.applyCardCategoryColor(clone);
        // Default quantity to 2, but clamp to available stock (max attribute)
        const qtyInput = clone.querySelector('.qty-input');
        if (qtyInput) {
          const maxQty = parseInt(qtyInput.max, 10) || 2;
          qtyInput.value = Math.min(2, maxQty);
        }
        this.ticketsPanel.appendChild(clone);
      });

      this.bindTicketEvents();
      this.inlineTrustLine();
      this.rewriteBuyNow();
      this.applySidebarSort();
      this.updatePriceLabelVisibility('box-loge');
      this.scrollToFirstCard();
    }

    // Setup sort controls for sidebar
    setupSortControls() {
      const select = document.getElementById('sidebar-sort-select');
      if (!select) return;

      select.addEventListener('change', () => {
        this.applySidebarSort();
      });
    }

    // Apply current sort to sidebar cards
    applySidebarSort() {
      if (!this.ticketsPanel) return;
      const select = document.getElementById('sidebar-sort-select');
      if (!select) return;

      const value = select.value;
      const cards = Array.from(this.ticketsPanel.querySelectorAll('.ticket-variant-card'));
      if (cards.length === 0) return;

      if (value === 'price-asc') {
        cards.sort((a, b) => (parseFloat(a.dataset.price) || 0) - (parseFloat(b.dataset.price) || 0));
      } else if (value === 'price-desc') {
        cards.sort((a, b) => (parseFloat(b.dataset.price) || 0) - (parseFloat(a.dataset.price) || 0));
      } else {
        // Default order - sort by data-sort-order if present
        cards.sort((a, b) => (parseInt(a.dataset.sortOrder) || 0) - (parseInt(b.dataset.sortOrder) || 0));
      }

      cards.forEach(card => this.ticketsPanel.appendChild(card));
    }

    // Render "From $X" price labels on the SVG map
    // Build index: "category|block" → cheapest price in cents
    buildBlockPriceIndex() {
      const index = {};
      const cards = this.getOriginalTicketCards();
      cards.forEach(card => {
        const block = (card.getAttribute('data-block') || '').toLowerCase();
        const cat = (card.getAttribute('data-category') || '').toLowerCase();
        const price = parseFloat(card.getAttribute('data-price'));
        if (!block || isNaN(price)) return;
        const key = cat + '|' + block;
        if (index[key] == null || price < index[key]) {
          index[key] = price;
        }
      });
      return index;
    }

    // Format price for map pill (compact, with EUR support)
    formatMapPrice(cents) {
      if (window.CurrencyConverter && window.CurrencyConverter.isEurMode()) {
        return window.CurrencyConverter.convert(cents);
      }
      const dollars = Math.round(cents / 100);
      return '$' + dollars.toLocaleString('en-US');
    }

    // Render "From $X" pills on the map — one per category+tribune group so
    // every purchasable area shows a price signal even when listings carry no
    // block number. Block-level prices (when present) refine the tribune price.
    renderPriceLabels() {
      if (!this.map) return;
      const svgNS = 'http://www.w3.org/2000/svg';

      const existing = this.map.querySelector('#price-labels-group');
      if (existing) existing.remove();

      const group = document.createElementNS(svgNS, 'g');
      group.id = 'price-labels-group';
      group.setAttribute('pointer-events', 'none');

      const priceIndex = this.buildBlockPriceIndex();

      // Group polygons by category+tribune, pick largest polygon for placement
      const tribuneGroups = {};
      if (this.sections) {
        this.sections.forEach(section => {
          const cat = (section.dataset.category || '').toLowerCase();
          if (!CATEGORIES[cat]) return; // skip non-purchasable areas (Presidential)
          const pr = this.priceRanges[cat];
          if (!pr || pr.count === 0) return; // sold out — greyed, no pill
          const tribune = (section.dataset.tribune || '').toLowerCase();
          const block = (section.dataset.block || '').toLowerCase();
          let bbox;
          try {
            bbox = section.getBBox();
          } catch (e) { return; }
          const area = bbox.width * bbox.height;
          const key = cat + '|' + tribune;
          if (!tribuneGroups[key]) {
            tribuneGroups[key] = { cat, blocks: [], bbox, area };
          }
          tribuneGroups[key].blocks.push(block);
          if (area > tribuneGroups[key].area) {
            tribuneGroups[key].bbox = bbox;
            tribuneGroups[key].area = area;
          }
        });
      }

      // First pass: compute pill geometry
      const pills = [];
      Object.values(tribuneGroups).forEach(({ cat, blocks, bbox }) => {
        // Cheapest block-level price in this tribune, else category minimum
        let cents = null;
        blocks.forEach(b => {
          const p = priceIndex[cat + '|' + b];
          if (p != null && (cents == null || p < cents)) cents = p;
        });
        if (cents == null) cents = Math.round(this.priceRanges[cat].min * 100);

        const labelText = 'From ' + this.formatMapPrice(cents);
        const pillHeight = 24;
        const pillWidth = Math.max(54, labelText.length * 7.5 + 18);
        pills.push({
          x: bbox.x + bbox.width / 2 - pillWidth / 2,
          y: bbox.y + bbox.height / 2 - pillHeight / 2,
          w: pillWidth,
          h: pillHeight,
          labelText
        });
      });

      // Collision pass: nudge overlapping pills apart (adjacent rings collide)
      for (let iter = 0; iter < 4; iter++) {
        let moved = false;
        for (let i = 0; i < pills.length; i++) {
          for (let j = i + 1; j < pills.length; j++) {
            const a = pills[i];
            const b = pills[j];
            const ox = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
            const oy = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
            if (ox > 0 && oy > 0) {
              moved = true;
              if (oy <= ox) {
                const shift = oy / 2 + 3;
                if (a.y <= b.y) { a.y -= shift; b.y += shift; } else { a.y += shift; b.y -= shift; }
              } else {
                const shift = ox / 2 + 3;
                if (a.x <= b.x) { a.x -= shift; b.x += shift; } else { a.x += shift; b.x -= shift; }
              }
            }
          }
        }
        if (!moved) break;
      }

      pills.forEach(({ x, y, w, h, labelText }) => {
        const pillHeight = h;
        const fo = document.createElementNS(svgNS, 'foreignObject');
        fo.setAttribute('x', x);
        fo.setAttribute('y', y);
        fo.setAttribute('width', w);
        fo.setAttribute('height', h);
        fo.setAttribute('pointer-events', 'none');
        fo.classList.add('fo-price-pill-fo');

        const div = document.createElement('div');
        div.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
        div.className = 'fo-price-pill';
        div.style.cssText =
          'display:flex;align-items:center;justify-content:center;' +
          'width:100%;height:100%;' +
          'background:rgba(17,24,39,0.92);' +
          'border:1.5px solid rgba(255,255,255,0.8);' +
          'border-radius:' + (pillHeight / 2) + 'px;' +
          'color:#fff;font-family:"Jost",system-ui,sans-serif;' +
          'font-size:12px;font-weight:800;line-height:1;' +
          'box-shadow:0 2px 6px rgba(0,0,0,0.4);white-space:nowrap;' +
          'padding:0 7px;';
        div.textContent = labelText;
        fo.appendChild(div);
        group.appendChild(fo);
      });

      this.map.appendChild(group);
    }

    // Delivery line is rendered by Liquid (ticket-variants-table.liquid) for non-Celine products.
    // This method is kept as a no-op for compatibility — cloned cards already include the line.
    inlineTrustLine() { /* handled by Liquid */ }

    // Rewrite Buy Now button text to emotional CTA
    rewriteBuyNow() {
      const lang = document.documentElement.lang || 'en';
      let label = 'Get These Tickets';
      if (lang.startsWith('fr')) label = 'Obtenir ces billets';
      else if (lang.startsWith('es')) label = 'Conseguir estas entradas';

      const btns = this.ticketsPanel?.querySelectorAll('.buy-now-btn') || [];
      btns.forEach(btn => {
        if (btn.dataset.relabeled) return;
        btn.childNodes.forEach(node => {
          if (node.nodeType === 3 && node.nodeValue.trim()) {
            node.nodeValue = ' ' + label + ' ';
          }
        });
        btn.dataset.relabeled = '1';
      });
    }

    // Scroll to first card in the sidebar panel
    scrollToFirstCard() {
      if (!this.ticketsPanel) return;
      const first = this.ticketsPanel.querySelector('.ticket-variant-card');
      if (first) {
        first.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    // Show/hide price labels based on active category
    updatePriceLabelVisibility(activeCategory) {
      const group = document.getElementById('price-labels-group');
      if (!group) return;

      // Hide labels when a category is selected (tooltip handles it), show all on clear
      group.style.opacity = activeCategory ? '0' : '1';
    }

    // Bind events to cloned tickets using event delegation
    bindTicketEvents() {
      if (!this.ticketsPanel) return;

      // Add hover events for box-level highlighting
      const cards = this.ticketsPanel.querySelectorAll('.ticket-variant-card');
      cards.forEach(card => {
        card.addEventListener('mouseenter', () => {
          const boxName = this.detectCardBox(card);
          const cardBlock = (card.getAttribute('data-block') || '').toLowerCase();
          const cardCat = (card.getAttribute('data-category') || '').toLowerCase();

          if (boxName) {
            // Temporarily highlight just this box
            this.sections.forEach(section => {
              section.classList.remove('active', 'dimmed');
              if (section.dataset.block === boxName) {
                section.classList.add('active');
              } else {
                section.classList.add('dimmed');
              }
            });
          } else if (cardBlock) {
            // Highlight the specific block + category combo (avoids Cat Gold/Cat 1 overlap)
            this.sections.forEach(section => {
              section.classList.remove('active', 'dimmed');
              const secBlock = (section.dataset.block || '').toLowerCase();
              const secCat = (section.dataset.category || '').toLowerCase();
              if (secBlock === cardBlock && secCat === cardCat) {
                section.classList.add('active');
              } else {
                section.classList.add('dimmed');
              }
            });
          } else {
            // Fallback: highlight by category
            const category = this.detectCardCategory(card.textContent);
            if (category) {
              this.sections.forEach(section => {
                section.classList.remove('active', 'dimmed');
                if (section.dataset.category === category) {
                  section.classList.add('active');
                } else {
                  section.classList.add('dimmed');
                }
              });
            }
          }
        });

        card.addEventListener('mouseleave', () => {
          // Restore previous state
          if (this.activeBlock) {
            this.sections.forEach(section => {
              section.classList.remove('active', 'dimmed');
              if (section.dataset.block === this.activeBlock) {
                section.classList.add('active');
              } else {
                section.classList.add('dimmed');
              }
            });
          } else if (this.activeCategory) {
            this.sections.forEach(section => {
              section.classList.remove('active', 'dimmed');
              if (section.dataset.category === this.activeCategory) {
                section.classList.add('active');
              } else {
                section.classList.add('dimmed');
              }
            });
          } else {
            this.sections.forEach(section => {
              section.classList.remove('active', 'dimmed');
            });
          }
        });
      });

      // Remove old event listener if exists (using capture phase)
      if (this._ticketPanelClickHandler) {
        this.ticketsPanel.removeEventListener('click', this._ticketPanelClickHandler, true);
      }

      // Use event delegation for all button clicks
      this._ticketPanelClickHandler = async (e) => {
        const target = e.target.closest('button');
        if (!target) return;

        console.log('[SeatingMap] Button clicked:', target.className);

        e.preventDefault();
        e.stopImmediatePropagation(); // Prevent other handlers from firing

        const card = target.closest('.ticket-variant-card');
        if (!card) {
          console.log('[SeatingMap] No card found for button');
          return;
        }

        // Get quantity input
        const qtyInput = card.querySelector('.qty-input');
        console.log('[SeatingMap] qtyInput found:', !!qtyInput, 'value:', qtyInput?.value, 'max:', qtyInput?.max);
        const getQuantity = () => parseInt(qtyInput?.value) || 2;
        const maxQty = parseInt(qtyInput?.max) || 10;

        // Helper function to update subtotal display
        const updateSubtotal = () => {
          const quantity = getQuantity();
          const priceElement = card.querySelector('.price-value');
          const subtotalValue = card.querySelector('[data-subtotal-value]');
          const subtotalQty = card.querySelector('.subtotal-qty');
          const subtotalRow = card.querySelector('.subtotal-row');

          if (priceElement && subtotalValue && subtotalQty) {
            let unitPrice = parseFloat(priceElement.dataset.unitPrice);
            if (isNaN(unitPrice)) {
              unitPrice = parseFloat(priceElement.textContent.replace(/[^0-9.]/g, ''));
            }

            const subtotal = unitPrice * quantity;

            // Add updating animation
            if (subtotalRow) {
              subtotalRow.classList.add('updating');
              setTimeout(() => subtotalRow.classList.remove('updating'), 200);
            }

            // Update display with formatted price
            subtotalValue.textContent = '$' + subtotal.toLocaleString('en-US', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            });
            subtotalQty.textContent = `(${quantity} ticket${quantity !== 1 ? 's' : ''})`;
          }
        };

        // Handle quantity decrease
        if (target.classList.contains('qty-decrease') || target.classList.contains('minus')) {
          const currentQty = getQuantity();
          console.log('[SeatingMap] Decrease clicked, currentQty:', currentQty, 'qtyInput:', !!qtyInput);
          if (currentQty > 1 && qtyInput) {
            qtyInput.value = currentQty - 1;
            updateSubtotal();
            console.log('[SeatingMap] Decreased to:', qtyInput.value);
          }
          return;
        }

        // Handle quantity increase
        if (target.classList.contains('qty-increase') || target.classList.contains('plus')) {
          const currentQty = getQuantity();
          console.log('[SeatingMap] Increase clicked, currentQty:', currentQty, 'maxQty:', maxQty, 'qtyInput:', !!qtyInput);
          if (currentQty < maxQty && qtyInput) {
            qtyInput.value = currentQty + 1;
            updateSubtotal();
            console.log('[SeatingMap] Increased to:', qtyInput.value);
          }
          return;
        }

        // Handle Add to Cart
        if (target.classList.contains('add-to-cart-btn')) {
          const variantId = target.dataset.variantId;
          const quantity = getQuantity();

          // Validate variant ID
          if (!variantId || variantId === 'undefined' || variantId === '') {
            console.error('Missing variant ID on button:', target);
            alert('Unable to add to cart: Missing product information. Please refresh the page and try again.');
            return;
          }

          // Disable button and show loading
          const originalText = target.innerHTML;
          target.disabled = true;
          target.innerHTML = '<span>Adding...</span>';

          try {
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                items: [{
                  id: parseInt(variantId, 10),
                  quantity: quantity
                }]
              })
            });

            // Parse response body for error details
            const responseData = await response.json().catch(() => null);

            if (response.ok) {
              // Update button temporarily
              target.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg> Added!';

              // On mobile, redirect to cart
              if (window.innerWidth <= 768) {
                window.location.href = '/cart';
                return;
              }

              // Open cart drawer - fetch fresh content first, then open
              try {
                // Fetch the updated cart drawer section HTML
                const sectionsResponse = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=cart-drawer`);
                const sectionsData = await sectionsResponse.json();

                const cartDrawer = document.querySelector('cart-drawer');
                if (cartDrawer && sectionsData['cart-drawer']) {
                  // Update drawer content with fresh HTML
                  const tempDiv = document.createElement('div');
                  tempDiv.innerHTML = sectionsData['cart-drawer'];
                  const newDrawerContent = tempDiv.querySelector('cart-drawer');
                  if (newDrawerContent) {
                    cartDrawer.innerHTML = newDrawerContent.innerHTML;
                  }

                  // Small delay to ensure DOM is updated, then open
                  await new Promise(resolve => setTimeout(resolve, 50));

                  // Open the drawer
                  if (typeof cartDrawer.show === 'function') {
                    cartDrawer.show();
                  } else if (typeof cartDrawer.open === 'function') {
                    cartDrawer.open();
                  } else {
                    cartDrawer.setAttribute('open', '');
                    cartDrawer.classList.add('is-open');
                  }
                }

                // Also dispatch event for any listeners
                const cartResponse = await fetch('/cart.js');
                const cartData = await cartResponse.json();
                cartData.sections = sectionsData;
                document.dispatchEvent(new CustomEvent('variant:add', {
                  bubbles: true,
                  detail: { items: [], cart: cartData }
                }));
              } catch (drawerError) {
                console.warn('Cart drawer open failed:', drawerError);
                // Fallback: redirect to cart page
                window.location.href = '/cart';
              }

              // Reset button after delay
              setTimeout(() => {
                target.innerHTML = originalText;
                target.disabled = false;
                if (qtyInput) {
                  const maxQty = parseInt(qtyInput.max, 10) || 2;
                  qtyInput.value = Math.min(2, maxQty);
                }
              }, 2000);
            } else {
              // Log detailed error info for debugging
              console.error('Cart add failed:', {
                status: response.status,
                statusText: response.statusText,
                variantId: variantId,
                quantity: quantity,
                response: responseData
              });

              // Provide user-friendly error message
              const errorMessage = responseData?.description || responseData?.message || 'Failed to add to cart';
              throw new Error(errorMessage);
            }
          } catch (error) {
            console.error('Error adding to cart:', error);

            // Show specific error message
            const errorText = error.message.includes('variant') || error.message.includes('product')
              ? 'Item unavailable'
              : 'Error - Try Again';
            target.innerHTML = errorText;

            setTimeout(() => {
              target.innerHTML = originalText;
              target.disabled = false;
            }, 2000);
          }
        }

        // Handle Buy Now - Add to cart then redirect to checkout
        if (target.classList.contains('buy-now-btn')) {
          const variantId = target.dataset.variantId;
          const quantity = getQuantity();

          // Validate variant ID
          if (!variantId || variantId === 'undefined' || variantId === '') {
            console.error('Missing variant ID on Buy Now button:', target);
            alert('Unable to proceed: Missing product information. Please refresh the page and try again.');
            return;
          }

          // Disable button and show loading
          const originalText = target.innerHTML;
          target.disabled = true;
          target.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" class="animate-spin"><path d="M12 2v4m0 12v4m10-10h-4M6 12H2"/></svg> Processing...';

          try {
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                items: [{
                  id: parseInt(variantId, 10),
                  quantity: quantity
                }]
              })
            });

            if (response.ok) {
              target.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg> Redirecting...';
              // Redirect to cart (user can review before checkout)
              const localeRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
              window.location.href = localeRoot.replace(/\/$/, '') + '/cart';
            } else {
              const responseData = await response.json().catch(() => null);
              const errorMessage = responseData?.description || responseData?.message || 'Failed to add to cart';
              throw new Error(errorMessage);
            }
          } catch (error) {
            console.error('[BuyNow] Error:', error);
            target.innerHTML = 'Error - Try Again';
            setTimeout(() => {
              target.innerHTML = originalText;
              target.disabled = false;
            }, 2000);
          }
        }
      };

      this.ticketsPanel.addEventListener('click', this._ticketPanelClickHandler, true);
    }
  }

  // Initialize when DOM is ready
  let _smInstance;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { _smInstance = new SeatingMap(); });
  } else {
    _smInstance = new SeatingMap();
  }

  // Re-render price labels when EUR rate is fetched
  document.addEventListener('currency:ready', () => {
    if (_smInstance && _smInstance.map) {
      _smInstance.renderPriceLabels();
    }
  });
})();

// cache-bust 1786300001 (Phase 1 UX: tribune price pills, sold-out greying, tooltip counts)
